import React, { CSSProperties, ReactNode, useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import {
    AnnotationFrameChange,
    AnnotationPlane,
    AnnotationRecord,
} from '~/domain/annotation/annotation';
import { PointMm } from '~/domain/geometry/patientGeometry';
import { paneImageOf } from '~/engine/view/paneImage';
import {
    arePointsOnDisplayedSlice,
    displayedSliceToleranceMm,
    imageToPaneFrame,
    viewShowsPlane,
} from '~/engine/view/planeViewGeometry';
import { PlaneView } from '~/engine/view/views/PlaneView';
import { usePlaneRedrawTick } from '~/hooks/viewer/annotationOverlay';
import { viewManagerState } from '~/state/atoms/engineAtoms';
import { selectedToolState, Tools } from '~/state/atoms/toolsAtoms';
import AnnotationOverlaySvg from './AnnotationOverlaySvg';

/** How one annotation is drawn right now, resolved once per render. */
export interface AnnotationAppearance {
    /** Drawn solid on the slice it lies on, dimmed and dashed elsewhere. */
    readonly onCurrentSlice: boolean;
    /** Hovered in the panel or being dragged: full strength, thicker. */
    readonly emphasized: boolean;
    /** Edit handles are offered (tool active, annotation on this slice). */
    readonly editable: boolean;
}

/**
 * Style for a text label drawn over the image: a dark outline behind the
 * letters keeps it readable on bright and dark anatomy alike, and fixed-width
 * digits stop a number that changes during a drag from jumping sideways.
 */
export const OVERLAY_LABEL_STYLE: CSSProperties = {
    paintOrder: 'stroke',
    stroke: 'rgba(0,0,0,0.75)',
    strokeWidth: 3,
    userSelect: 'none',
    fontVariantNumeric: 'tabular-nums',
};

interface AnnotationPlaneOverlayProps<
    TItem extends AnnotationRecord,
    TDraft extends { readonly plane: AnnotationPlane },
    TDrag,
> {
    view: PlaneView | null;
    items: readonly TItem[];
    /** The in-progress placement, drawn only in the view it is bound to. */
    draft: TDraft | null;
    /**
     * True while a shape is being placed, of this kind or of the other kind
     * the same tool places. No handles are offered meanwhile: a circle handle
     * grabbed during a line placement would start a drag while the engine
     * still expects the next click of the placement.
     */
    placementActive: boolean;
    /** Tool whose selection offers the edit handles. */
    tool: Tools;
    /** Hovered in the panel, or null. */
    highlightedId: string | null;
    /** Being dragged in this view, or null. */
    draggingId: string | null;
    beginDrag: (event: React.MouseEvent, drag: TDrag) => void;
    /** `item` moved by `change`: its plane and every point. */
    itemInFrame: (item: TItem, change: AnnotationFrameChange) => TItem;
    /** `draft` moved by `change`: its plane and every point. */
    draftInFrame: (draft: TDraft, change: AnnotationFrameChange) => TDraft;
    /** The points whose distance to the shown slice decides "on this slice". */
    pointsOf: (item: TItem) => readonly PointMm[];
    renderItem: (
        view: PlaneView,
        item: TItem,
        appearance: AnnotationAppearance,
        beginDrag: (event: React.MouseEvent, drag: TDrag) => void
    ) => ReactNode;
    renderDraft: (view: PlaneView, draft: TDraft) => ReactNode;
}

/**
 * The shared part of the line and circle overlays. It keeps the annotations
 * this view shows, decides for each whether it is drawn on-slice, selected and
 * editable, draws the shape being placed, and leaves drawing one annotation
 * to the caller.
 *
 * The annotations of the series the pane shows are drawn, primary or fused
 * secondary. Each is stored in its image's own frame and is handed to
 * `renderItem` and `pointsOf` moved onto the pane through the registration
 * read now, so the geometry a caller sees, and a drag it starts, is in the
 * pane's frame.
 *
 * Shared so that one on-slice test decides both the styling and the handles;
 * two copies could drift and offer a handle on an annotation whose plane the
 * view is not showing. The measurement overlay does not use it, because it
 * draws its editable pass after the draft.
 */
export default function AnnotationPlaneOverlay<
    TItem extends AnnotationRecord,
    TDraft extends { readonly plane: AnnotationPlane },
    TDrag,
>({
    view,
    items,
    draft,
    placementActive,
    tool,
    highlightedId,
    draggingId,
    beginDrag,
    itemInFrame,
    draftInFrame,
    pointsOf,
    renderItem,
    renderDraft,
}: AnnotationPlaneOverlayProps<TItem, TDraft, TDrag>) {
    const selectedTool = useRecoilValue(selectedToolState);
    const viewManager = useRecoilValue(viewManagerState);
    const renderTick = usePlaneRedrawTick(viewManager);

    const content = useMemo(() => {
        if (!view) {
            return null;
        }
        const pane = paneImageOf(view);
        if (pane.kind === 'none') {
            return null;
        }
        const ontoPane = imageToPaneFrame(pane);
        const toleranceMm = displayedSliceToleranceMm(view);
        const visible = items
            .filter((item) => item.visible && viewShowsPlane(view, item.plane))
            .map((item) => itemInFrame(item, ontoPane));
        const draftOnView =
            draft && viewShowsPlane(view, draft.plane)
                ? draftInFrame(draft, ontoPane)
                : null;
        if (visible.length === 0 && !draftOnView) {
            return null;
        }
        const editingEnabled = selectedTool === tool && !placementActive;
        return (
            <>
                {visible.map((item) => {
                    const onSlice = arePointsOnDisplayedSlice(
                        pointsOf(item),
                        view,
                        item.plane,
                        toleranceMm
                    );
                    return renderItem(
                        view,
                        item,
                        {
                            onCurrentSlice: onSlice,
                            emphasized:
                                item.id === highlightedId ||
                                item.id === draggingId,
                            editable: editingEnabled && onSlice,
                        },
                        beginDrag
                    );
                })}
                {draftOnView && renderDraft(view, draftOnView)}
            </>
        );
        // renderTick refreshes the projections on slice / pan / zoom /
        // registration change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        view,
        items,
        draft,
        placementActive,
        tool,
        selectedTool,
        highlightedId,
        draggingId,
        beginDrag,
        itemInFrame,
        draftInFrame,
        pointsOf,
        renderItem,
        renderDraft,
        renderTick,
    ]);

    if (!content) {
        return null;
    }
    return <AnnotationOverlaySvg>{content}</AnnotationOverlaySvg>;
}
